import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, CheckCircle, RefreshCw, Play, Filter,
  TrendingUp, TrendingDown, Clock, Zap
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Anomalies = () => {
  const { shouldShowData } = useDemoData();

  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [anomalies, setAnomalies] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    fetchAnomalies();
  }, [activeTab]);

  const fetchAnomalies = async () => {
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await axios.get(`${API_URL}/anomalies${status ? `?status=${status}` : ''}`);
      setAnomalies(response.data);
    } catch (error) {
      console.error('Failed to fetch anomalies:', error);
      toast.error('Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  };

  const runDetection = async () => {
    setDetecting(true);
    try {
      const response = await axios.post(`${API_URL}/anomalies/detect`);
      toast.success(`Detection complete: ${response.data.detected} anomalies found`);
      fetchAnomalies();
    } catch (error) {
      toast.error('Failed to run anomaly detection');
    } finally {
      setDetecting(false);
    }
  };

  const resolveAnomaly = async (anomalyId) => {
    try {
      await axios.put(`${API_URL}/anomalies/${anomalyId}/resolve`);
      toast.success('Anomaly marked as resolved');
      fetchAnomalies();
    } catch (error) {
      toast.error('Failed to resolve anomaly');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800';
      case 'high': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'medium': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800';
      default: return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  const getSeverityBarColor = (severity) => {
    switch (severity) {
      case 'critical': return '#E11D48';
      case 'high': return '#F59E0B';
      case 'medium': return '#0EA5E9';
      default: return '#64748B';
    }
  };

  // Prepare chart data
  const chartData = anomalies.slice(0, 10).map(a => ({
    service: a.service.length > 15 ? a.service.substring(0, 15) + '...' : a.service,
    deviation: Math.abs(a.deviation_percent),
    severity: a.severity
  }));

  // Stats
  const stats = {
    total: anomalies.length,
    critical: anomalies.filter(a => a.severity === 'critical').length,
    high: anomalies.filter(a => a.severity === 'high').length,
    medium: anomalies.filter(a => a.severity === 'medium').length,
    low: anomalies.filter(a => a.severity === 'low').length,
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Anomalies" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="anomalies-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </Card>
          ))}
        </div>
        <Card className="p-6">
          <Skeleton className="h-64 w-full" />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="anomalies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white"><BrandTitle>Anomaly Detection</BrandTitle></h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Monitor and investigate cost anomalies across your cloud infrastructure
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={runDetection}
            disabled={detecting}
            data-testid="run-detection-btn"
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {detecting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Run Detection
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-hover" data-testid="stat-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                <AlertTriangle className="w-5 h-5 text-slate-400 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-critical">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                <Zap className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{stats.critical}</p>
                <p className="text-xs text-slate-400">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-high">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.high}</p>
                <p className="text-xs text-slate-400">High</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-medium">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-sky-900/30">
                <TrendingDown className="w-5 h-5 text-emerald-600 dark:text-sky-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600 dark:text-sky-400">{stats.medium}</p>
                <p className="text-xs text-slate-400">Medium</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-low">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                <Clock className="w-5 h-5 text-slate-400 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-400 dark:text-slate-400">{stats.low}</p>
                <p className="text-xs text-slate-400">Low</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card data-testid="anomaly-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Deviation by Service</CardTitle>
            <CardDescription>Cost deviation percentage from expected values</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" unit="%" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis dataKey="service" type="category" tick={{ fontSize: 11 }} width={100} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(1)}%`, 'Deviation']}
                    contentStyle={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  />
                  <Bar dataKey="deviation" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getSeverityBarColor(entry.severity)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Anomalies List */}
      <Card data-testid="anomalies-list">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Anomaly Details</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
                <TabsTrigger value="resolved" data-testid="tab-resolved">Resolved</TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {anomalies.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No anomalies found</p>
                <p className="text-sm mt-1">Run detection to scan for cost anomalies</p>
              </div>
            ) : (
              anomalies.map((anomaly, idx) => (
                <div 
                  key={anomaly.id}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all"
                  data-testid={`anomaly-item-${idx}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={`${getSeverityColor(anomaly.severity)}`}>
                          {anomaly.severity}
                        </Badge>
                        <Badge variant="outline" className={anomaly.status === 'active' ? 'border-amber-500 text-amber-600' : 'border-emerald-500 text-emerald-600'}>
                          {anomaly.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">{anomaly.service}</h3>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400">Expected</p>
                          <p className="font-medium text-slate-900 dark:text-white">{formatCurrency(anomaly.expected_cost)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Detected</p>
                          <p className="font-medium text-slate-900 dark:text-white">{formatCurrency(anomaly.detected_cost)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Deviation</p>
                          <p className={`font-medium ${anomaly.deviation_percent > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {anomaly.deviation_percent > 0 ? '+' : ''}{anomaly.deviation_percent.toFixed(1)}%
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400">Detected At</p>
                          <p className="font-medium text-slate-900 dark:text-white text-xs">
                            {new Date(anomaly.detected_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    {anomaly.status === 'active' && isAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => resolveAnomaly(anomaly.id)}
                        data-testid={`resolve-btn-${idx}`}
                        className="gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Anomalies;
